import { inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { MsalService } from '@azure/msal-angular';
import { AuthError } from '@azure/msal-browser';
import { SeverityLevel } from '@microsoft/applicationinsights-web';
import { Store } from '@ngrx/store';

import { AppState } from 'src/app/store';
import { TelemetryService } from 'src/app/core/telemetry/telemetry.service';
import { environment } from 'src/environments/environment';

import { MsalAuthActions } from './store/actions/msal-auth.actions';
import { AuthenticationStatus } from './store/state/msal-auth.state';
import { parseCustomErrorCode } from './utils/error-parser.utils';

async function handleRedirectResult(
  msalService: MsalService,
  store: Store<AppState>,
): Promise<boolean> {
  const result = await msalService.instance.handleRedirectPromise();

  if (result?.account) {
    msalService.instance.setActiveAccount(result.account);

    store.dispatch(
      MsalAuthActions.setAuthenticationState({
        status: AuthenticationStatus.Authenticated,
        accountInfo: result.account,
      }),
    );
    return true;
  }

  return false;
}

async function handleCachedAccount(
  msalService: MsalService,
  store: Store<AppState>,
): Promise<boolean> {
  const activeAccount = msalService.instance.getActiveAccount();

  if (!activeAccount) {
    return false;
  }

  try {
    const tokenResponse = await msalService.instance.acquireTokenSilent({
      account: activeAccount,
      scopes: environment.msal.consentScopes,
    });

    store.dispatch(
      MsalAuthActions.setAuthenticationState({
        status: AuthenticationStatus.Authenticated,
        accountInfo: tokenResponse.account,
      }),
    );
    return true;
  } catch {
    return false;
  }
}

export async function initializeMsal(): Promise<void> {
  const msalService = inject(MsalService);
  const platformId = inject(PLATFORM_ID);
  const store = inject(Store<AppState>);

  if (!isPlatformBrowser(platformId)) {
    return;
  }

  const telemetry = inject(TelemetryService);
  try {
    await msalService.instance.initialize();

    const hasRedirectResult = await handleRedirectResult(msalService, store);
    if (hasRedirectResult) {
      return;
    }

    const hasCachedAccount = await handleCachedAccount(msalService, store);
    if (hasCachedAccount) {
      return;
    }

    store.dispatch(
      MsalAuthActions.setAuthenticationState({
        status: AuthenticationStatus.Unauthenticated,
        accountInfo: null,
      }),
    );
  } catch (error) {
    telemetry.trackException(
      {
        exception: error as Error,
        severityLevel: SeverityLevel.Error,
      },
      {
        component: 'MsalInitializer',
        method: 'initializeMsal',
      },
    );

    let payload: AuthError | { message: string };
    if (error instanceof AuthError) {
      const errorCode = parseCustomErrorCode(error.errorMessage);
      payload = { ...error, errorCode: errorCode || error.errorCode };
    } else {
      payload = { message: String(error) };
    }

    store.dispatch(
      MsalAuthActions.handleAuthError({
        error: payload,
      }),
    );
  }
}
